import { Phone, Star } from 'lucide-react'
import type { FC, MouseEvent } from 'react'
import { Link, type To } from 'react-router'
import { Avatar } from './avatar'
import { DollarRating } from './dollar-rating'
import { PinIcon } from './icons'
import { formatPhoneNumber, formatTelHref } from './phone'

/** Fields from `searchProviders` recommendations used by the results card. */
export type ProviderCardProvider = {
  name?: string | null | undefined
  credentials?: string | null | undefined
  specialty?: string | null | undefined
  avatarUrl?: string | null | undefined
  distanceInMiles?: number | null | undefined
  facilityName?: string | null | undefined
  address?: string | null | undefined
  rating?: number | null | undefined
  ratingsCount?: number | null | undefined
  efficiencyIndex?: number | null | undefined
  phone?: string | null | undefined
}

type ProviderCardProps = {
  provider: ProviderCardProvider
  /** Relative provider-detail route opened by the card body and “More Info”. */
  detailTo: To
}

/** First letters of the first two name words, uppercased — the avatar fallback. */
export const toInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('')

export const ProviderCard: FC<ProviderCardProps> = ({ provider, detailTo }) => {
  const {
    name,
    credentials,
    avatarUrl,
    distanceInMiles,
    facilityName,
    address,
    rating,
    ratingsCount,
    efficiencyIndex,
    phone,
  } = provider

  const hasFacilityName = facilityName != null && facilityName.trim() !== ''
  const hasAddress = address != null && address.trim() !== ''
  const hasLocationSummary = distanceInMiles != null || hasFacilityName || hasAddress
  const hasPhone = phone != null && phone.trim() !== ''
  const displayPhone = hasPhone ? (formatPhoneNumber(phone, false) ?? phone) : null

  const openPhone = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (phone != null && phone.trim() !== '') {
      window.location.href = formatTelHref(phone)
    }
  }

  const keepMoreInfoClickLocal = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation()
  }

  return (
    <article
      className="flex flex-col gap-3 rounded-2xl border border-secondary-200 bg-white has-hover:ring has-hover:ring-primary-400 has-hover:border-primary-400 has-hover:hover:shadow-lg shadow-secondary-800/10 transition-all duration-200"
      data-testid="provider-card"
    >
      <Link
        data-testid="provider-card-link"
        prefetch="viewport"
        to={detailTo}
        className="flex flex-col gap-3 no-underline text-inherit px-4 pt-4"
      >
        <header className="flex items-start gap-3">
          <Avatar url={avatarUrl} name={name} />
          <div className="min-w-0 flex-1">
            <h2
              className="truncate font-display text-xl font-semibold leading-tight text-secondary-800"
              data-testid="provider-card-name"
            >
              {name}
              {credentials != null && credentials.trim() !== '' && (
                <span className="ml-2 text-xs font-sans font-normal text-secondary-600">{credentials}</span>
              )}
            </h2>
            {(typeof rating === 'number' || efficiencyIndex != null) && (
              <div className="flex items-center gap-4 mt-1">
                {typeof rating === 'number' && (
                  <div className="flex items-center gap-1" data-testid="provider-card-ratings">
                    <Star className="size-4 stroke-none fill-primary-500" />
                    <span className="text-sm font-semibold text-secondary-800">{rating.toFixed(1)}</span>
                    {ratingsCount != null && <span className="text-sm text-secondary-700">({ratingsCount})</span>}
                  </div>
                )}
                {efficiencyIndex != null && (
                  <div className="flex items-center gap-1.5" data-testid="provider-card-cost">
                    <span className="text-sm text-secondary-800">Cost:</span>
                    <DollarRating efficiencyIndex={efficiencyIndex} className="text-sm font-semibold leading-none" />
                  </div>
                )}
              </div>
            )}
          </div>
        </header>

        {hasLocationSummary && (
          <div className="flex min-w-0 items-start gap-2">
            <PinIcon className="size-6 shrink-0 text-primary-500" />
            <div className="-mt-0.5 min-w-0 flex-1 line-clamp-2">
              {distanceInMiles != null && (
                <span
                  className="whitespace-nowrap text-sm font-bold text-secondary-800"
                  data-testid="provider-card-distance"
                >
                  {distanceInMiles.toFixed(1)} Mi&nbsp;&nbsp;
                </span>
              )}
              {hasFacilityName && (
                <span className="text-sm font-semibold text-secondary-700" data-testid="provider-card-facility-name">
                  {facilityName}
                </span>
              )}
              {hasAddress && (
                <div className="text-sm text-secondary-600" data-testid="provider-card-address">
                  {address}
                </div>
              )}
            </div>
          </div>
        )}
      </Link>

      <div className="mt-auto flex flex-wrap gap-2 px-4 pb-4" data-testid="provider-card-actions">
        {displayPhone != null && (
          <button
            type="button"
            className="w-full xs:w-auto btn btn-secondary group"
            data-testid="provider-card-phone"
            onClick={openPhone}
          >
            <Phone className="size-5 stroke-0 fill-primary-500 group-hover:fill-white" />
            {displayPhone}
          </button>
        )}
        <Link
          to={detailTo}
          prefetch="viewport"
          onClick={keepMoreInfoClickLocal}
          data-testid="provider-card-more-info"
          className="w-full xs:w-auto btn btn-primary btn-outline"
        >
          More Info
        </Link>
      </div>
    </article>
  )
}
